import classNames from 'classnames';
import { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { memoizedComponent } from 'utils/memoizeComponent';

import { Tab, TabsProps, useTabs } from '../core/useTabs';
import './index.css';

const TabsV2 = <T extends Tab>(props: TabsProps<T>) => {
  const {
    tabs,
    onBlur,
    className = '',
    tabClassName = '',
    swipable = false,
    direction = 'horizontal',
    slidesPerView = 'auto',
    activeTabClassName = '',
    tabTestAttr = 'tab',
  } = props;

  const { activeTab: _activeTab, ...swiperProps } = useMemo(() => props, [props]);

  const { activeTab, handleTabClick } = useTabs({
    ...props,
    activeTab: _activeTab,
  });

  const wrapperClassName = classNames({
    'tabs-v2': true,
    [className]: className,
  });

  return (
    <div className={wrapperClassName} data-test={props['data-test']} onBlur={onBlur} tabIndex={0}>
      <Swiper
        allowTouchMove={swipable}
        draggable={swipable}
        direction={direction}
        slidesPerView={slidesPerView}
        {...swiperProps}
        className={undefined}
      >
        {tabs.map(({ id, label, hidden, count }) => {
          if (hidden) return null;

          const itemClassName = classNames('tabs-v2__item', {
            'tabs-v2__item--active': id === activeTab,
            'tabs-v2__item--auto': slidesPerView === 'auto',
            [activeTabClassName]: id === activeTab,
            [tabClassName]: tabClassName,
          });

          return (
            <SwiperSlide
              key={id}
              className={itemClassName}
              onClick={() => handleTabClick(id)}
              data-test={`${tabTestAttr}-${id}`}
              data-active={id === activeTab}
            >
              {typeof label === 'function' ? label() : label}
              {count !== undefined && <span className='tabs-v2__item-count'>{count}</span>}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default memoizedComponent(TabsV2);
